/* eslint-disable import/no-unresolved */
import {
  KlevuConfig,
  KlevuFetch,
  suggestions,
} from 'https://cdn.skypack.dev/pin/@klevu/core@v1.1.5-0q1n6dBIy9DgOZifPmpX/mode=imports,min/optimized/@klevu/core.js'

/* eslint-enable import/no-unresolved */
const { debounce, trapFocus } = window.BAO.Utils

const config = {
  apiKey: `${window.theme.klevu.apiKey}`,
  url: `//${window.theme.klevu.url}/cs/v2/search`,
}

KlevuConfig.init(config)

class SearchDrawer extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false

    this.load()
  }

  setupListeners () {
    super.setupListeners()

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)
    this.onResize = this.onResize.bind(this)

    window.addEventListener('resize', this.onResize, { passive: true })

    this.listeners.add(document, 'drawerOpen', this.onDrawerOpen.bind(this))

    this.listeners.add(
      this.els.searchField.element,
      'input',
      debounce(this.onSearchType.bind(this), 100),
    )

    this.listeners.add(
      this.els.filterTrigger.element,
      'click',
      this.onFilterTriggerClick.bind(this),
    )

    this.listeners.add(
      this.els.viewTrigger.element,
      'click',
      this.onViewTriggerClick.bind(this),
    )

    if (this.els.imageLink?.exists) {
      this.listeners.add(
        this.els.imageLink.element,
        'click',
        this.onImageLinkClick.bind(this),
      )
    }
  }

  onResize () {
    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.measureDrawerWidth()
  }

  measureDrawerWidth () {
    setTimeout(() => {
      if (!this.parentDrawer.classList.contains(this.drawerActiveClass)) return

      document.documentElement.style.setProperty(
        '--SearchDrawer_ColumnWidth',
        `${this.offsetWidth}px`,
      )
    }, 1000)
  }

  onSuggestionClick (e) {
    e.preventDefault()

    this.loadAutoSuggestions(e.target.dataset.content)
  }

  load () {
    const queryParams = new URLSearchParams(window.location.search)

    if (queryParams.has('q')) {
      document
        .querySelector('.drw-Search_Loader')
        .setAttribute('aria-hidden', false)

      this.loadAutoSuggestions(queryParams.get('q'))
    }
  }

  async loadAutoSuggestions (term) {
    const result = await KlevuFetch(suggestions(term))

    this.suggestions =
      result.suggestionsById('suggestions')?.suggestions.map(i => i.suggest) ??
      []

    this.els.suggestionWrapper.element.setAttribute(
      'aria-hidden',
      this.suggestions.length === 0,
    )

    const map = this.suggestions
      .map(suggestion => {
        return `<li tabindex="-1"><a target="_self" href="?q=${suggestion.replace(
          /(<([^>]+)>)/gi,
          '',
        )}" data-content="${suggestion.replace(
          /(<([^>]+)>)/gi,
          '',
        )}" class="klevu-track-click" data-search-drawer-el="suggestion">${suggestion}</a></li>`
      })
      .join('')

    this.els.suggestionList.element.innerHTML = map

    document
      .querySelectorAll('[data-search-drawer-el="suggestion"]')
      .forEach(el => {
        this.listeners.add(el, 'click', this.onSuggestionClick.bind(this))
      })
  }

  onSearchType (e) {
    this.loadAutoSuggestions(e.target.value)
  }

  onViewTriggerClick () {
    if (document.querySelector('.drw-Drawer-search')) {
      document
        .querySelector('.drw-Drawer-search')
        .classList.toggle('drw-Drawer-singleProducts')
    }
  }

  onImageLinkClick () {
    window.dataLayer.push({
      event: 'searchImageClicked',
    })
  }

  onFilterTriggerClick () {
    this.toggleFiltersDrawer()
    this.focusFiltersDrawer()
    this.handleFiltersDrawerClose()
    this.handleFilterDrawerFocus()
  }

  toggleFiltersDrawer () {
    // Open filters drawer
    const searchDrawer = document.querySelector('.drw-Drawer-search')
    searchDrawer?.classList.toggle('drw-Drawer-filtersOpen')
  }

  focusFiltersDrawer () {
    // Apply focus to filters drawer
    const filtersDrawer = document.querySelector('.kuFilters')
    if (!filtersDrawer) return
    filtersDrawer.focus()
    trapFocus(filtersDrawer)
  }

  handleFiltersDrawerClose () {
    const searchDrawer = document.querySelector('.drw-Drawer-search')
    if (!searchDrawer) return

    const handlefiltersDrawerCloseClick = e => {
      // If element is close button
      if (!e.target.matches('[data-close-ku-filters]')) return

      // Refocus search drawer
      searchDrawer.focus()
      trapFocus(searchDrawer)

      // Remove event listener
      document.removeEventListener('click', handlefiltersDrawerCloseClick)
    }

    document.addEventListener('click', handlefiltersDrawerCloseClick)
  }

  handleFilterDrawerFocus () {
    const filtersDrawer = document.querySelector('.kuFilters')
    if (!filtersDrawer) return

    const handleOptionClick = e => {
      // If the element is an option, the filter button, or the clear all button...
      if (
        !e.target.matches(
          '.kuFilters :is(.klevuFilterOption, .kuFiltersSelectedFilterButton, .kuFilterTagClearAllQuick)',
        )
      ) {
        return
      }

      // Remove event listener
      filtersDrawer.removeEventListener('click', handleOptionClick)

      // Wait for elements to reload, refocus and re-run this method to re-apply event listener
      window.setTimeout(() => {
        this.focusFiltersDrawer()
        this.handleFilterDrawerFocus()
      }, 1000)
    }

    filtersDrawer.addEventListener('click', handleOptionClick)
  }

  onDrawerOpen (event) {
    if (event.detail.key !== 'search') return

    this.measureDrawerWidth()

    const interval = setInterval(() => {
      if (this.els.searchField.element === document.activeElement) {
        clearInterval(interval)
      }

      this.els.searchField.element.focus()
    }, 100)
  }

  get parentDrawer () {
    return this.externalEls['parent-drawer']?.element
  }

  get drawerActiveClass () {
    return `drw-Drawer-active`
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer)
}
